
import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useSessionStore } from '@/store/sessionStore';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import { Clipboard, Pencil, ExternalLink, ArrowLeft, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '../common/ThemeToggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const SessionInfo: React.FC = () => {
    const {
        currentSessionId,
        currentSessionDetails,
        updateSessionDefaults,
        projectPath,
        isDevelopment,
        cloneSession,
    } = useSessionStore();

    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [title, setTitle] = useState('');
    const titleInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setTitle(currentSessionDetails?.session_title || '');
    }, [currentSessionDetails?.session_title]);

    useEffect(() => {
        if (isEditingTitle) {
            titleInputRef.current?.focus();
            titleInputRef.current?.select();
        }
    }, [isEditingTitle]);

    const handleTitleUpdate = () => {
        if (!currentSessionId || !currentSessionDetails) return;
        if (title.trim() && title !== currentSessionDetails.session_title) {
            updateSessionDefaults({ session_title: title.trim() });
        } else if (!title.trim() && currentSessionDetails.session_title) {
            setTitle(currentSessionDetails.session_title);
        }
        setIsEditingTitle(false);
    };

    const handleCopySessionInfo = () => {
        if (currentSessionDetails?.session_title && currentSessionId) {
            const textToCopy = `${currentSessionDetails.session_title} - ${currentSessionId}`;
            navigator.clipboard.writeText(textToCopy).then(() => {
                toast.success("Session info copied to clipboard!");
            }).catch(() => {
                toast.error("Failed to copy session info.");
            });
        }
    };

    const handleCloneSession = async () => {
        if (!currentSessionId) {
            toast.error("No active session to clone.");
            return;
        }
        await cloneSession(currentSessionId);
    };

    return (
        <Card>
            <CardContent className="p-2 text-xs space-y-1">
                <p>Project: <span className="font-mono">{projectPath || '...'}</span></p>
                <div className="flex items-center gap-1 group">
                    {isEditingTitle ? (
                       <div className="flex items-center gap-1 flex-grow">
                            <Label htmlFor="session-title-input" className="whitespace-nowrap">Session Name:</Label>
                            <Input
                                ref={titleInputRef}
                                id="session-title-input"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                onBlur={handleTitleUpdate}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleTitleUpdate();
                                    if (e.key === 'Escape') {
                                        setTitle(currentSessionDetails?.session_title || '');
                                        setIsEditingTitle(false);
                                    }
                                }}
                                className="h-6 text-xs font-mono font-bold flex-grow p-1"
                                disabled={!currentSessionId}
                            />
                       </div>
                    ) : (
                        <div className="flex items-center gap-1 flex-grow overflow-hidden cursor-text" onClick={() => setIsEditingTitle(true)}>
                            <p className="whitespace-nowrap">Session Name:</p>
                            <p className="font-mono font-bold truncate">{currentSessionDetails?.session_title || '...'}</p>
                            <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                    )}
                    <TooltipProvider>
                        {currentSessionDetails?.url && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 flex-shrink-0"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(currentSessionDetails.url!, '_blank');
                                        }}
                                    >
                                        <ExternalLink className="h-3 w-3" />
                                        <span className="sr-only">Open session URL</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Open source URL</p>
                                </TooltipContent>
                            </Tooltip>
                        )}
                        {currentSessionDetails && currentSessionId && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 flex-shrink-0"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopySessionInfo();
                                        }}
                                    >
                                        <Clipboard className="h-3 w-3" />
                                        <span className="sr-only">Copy session info</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Copy session info</p>
                                </TooltipContent>
                            </Tooltip>
                        )}
                        {currentSessionId && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5 flex-shrink-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCloneSession();
                                            }}
                                        >
                                            <Copy className="h-3 w-3" />
                                            <span className="sr-only">Clone session</span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Clone Session</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </TooltipProvider>
                    {currentSessionDetails?.parent_session_id && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 flex-shrink-0"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            useSessionStore.getState().setCurrentSessionId(currentSessionDetails!.parent_session_id!);
                                        }}
                                    >
                                        <ArrowLeft className="h-3 w-3" />
                                        <span className="sr-only">Back to parent session</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Back to parent session</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    <ThemeToggle />
                </div>
                {isDevelopment && (
                    <div className="flex items-center gap-1 pt-1">
                        <span>Mode:</span> <Badge variant="secondary" className="text-xs">Development</Badge>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default SessionInfo;